/**
 * InvitationCard — the feature's front door.
 *
 * For most people this card IS the Projects feature: it is the first thing they
 * ever see of it, and whether they accept decides whether the workspace has a
 * group in it. So it answers all four questions a recipient actually has, in
 * order — what, from whom, from which organization, and why me — and then gives
 * two unambiguous actions.
 *
 * The inviter's own words sit in a quoted block below the system copy, visually
 * distinct so it never reads as boilerplate.
 */

import { useState } from 'react';
import { activeLocale } from '@/utils/locale';
import { useLanguage } from '@/hooks/useLanguage';
import type { ProjectInviteNotification } from '../types';
import { PersonAvatar } from './PersonAvatar';
import { ProjectIcon } from './ProjectIcon';
import './InvitationCard.css';

interface InvitationCardProps {
  notification: ProjectInviteNotification;
  /** Collapsed rows keep the list scannable; one card opens at a time. */
  expanded?: boolean;
  onToggle?: () => void;
  /** Resolve the invitation. Errors surface inline on the card itself. */
  onRespond: (memberId: string, accept: boolean) => Promise<void>;
  /** Open the project — offered once the invitation has been accepted. */
  onOpen: (projectId: string) => void;
}

/** "3 min ago" / "2 hours ago" — relative time reads better in a bell than a date. */
export function relativeTime(iso: string | null, t: (k: string) => string): string {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const seconds = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (seconds < 60) return t('time.justNow') || 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return (t('time.minutesAgo') || '{{n}}m ago').replace('{{n}}', String(minutes));
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return (t('time.hoursAgo') || '{{n}}h ago').replace('{{n}}', String(hours));
  const days = Math.floor(hours / 24);
  if (days < 7) return (t('time.daysAgo') || '{{n}}d ago').replace('{{n}}', String(days));
  return new Date(iso).toLocaleDateString(activeLocale());
}

export function InvitationCard({
  notification,
  expanded = true,
  onToggle,
  onRespond,
  onOpen,
}: InvitationCardProps) {
  const { t } = useLanguage();
  const [busy, setBusy] = useState<'accept' | 'decline' | null>(null);
  const [resolved, setResolved] = useState<'accepted' | 'declined' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const invite = notification.payload;
  const pending = notification.action_state === 'pending' && !resolved;

  const respond = async (accept: boolean) => {
    if (busy) return;
    setBusy(accept ? 'accept' : 'decline');
    setError(null);
    try {
      await onRespond(invite.member_id, accept);
      setResolved(accept ? 'accepted' : 'declined');
    } catch (err: any) {
      setError(
        err?.response?.data?.error
          || (t('projects.invite.respondFailed') || 'Could not answer this invitation.'),
      );
    } finally {
      setBusy(null);
    }
  };

  return (
    <article
      className={`invite-card ${notification.read ? '' : 'is-unread'}`}
      data-resolved={resolved || undefined}
    >
      <header
        className={`invite-card-top ${onToggle ? 'is-toggle' : ''}`}
        onClick={onToggle}
        onKeyDown={onToggle ? (e) => {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle(); }
        } : undefined}
        role={onToggle ? 'button' : undefined}
        tabIndex={onToggle ? 0 : undefined}
        aria-expanded={onToggle ? expanded : undefined}
      >
        <PersonAvatar
          seed={invite.member_id}
          name={invite.invited_by_name}
          tenantName={invite.invited_by_tenant_name}
          size={34}
        />
        <div className="invite-card-heading">
          <p className="invite-card-lede">
            <strong>{invite.invited_by_name || (t('projects.invite.someone') || 'Someone')}</strong>
            {' '}
            {t('projects.invite.invitedYou') || 'invited you to a shared project'}
          </p>
          {invite.invited_by_tenant_name && (
            <p className="invite-card-org">{invite.invited_by_tenant_name}</p>
          )}
        </div>
        <time className="invite-card-time" dateTime={notification.created_at || undefined} dir="auto">
          {relativeTime(notification.created_at, t)}
        </time>
      </header>

      {/* Collapsed rows show only who and when — the whole point of the list
          is to scan it. Opening one reveals the project, the message and the
          actions in place, without leaving the list. */}
      {!expanded && (
        <p dir="auto" className="invite-card-teaser">
          {invite.project_name}
          {notification.action_state !== 'pending' && (
            <span className={`pj-pill ${notification.action_state === 'accepted'
              ? 'pj-pill-accepted' : 'pj-pill-declined'}`}>
              {notification.action_state === 'accepted'
                ? (t('projects.invite.accepted') || 'Accepted')
                : (t('projects.invite.declined') || 'Declined')}
            </span>
          )}
        </p>
      )}

      {expanded && (
      <>
      <div className="invite-card-project">
        <span className="invite-card-project-icon" aria-hidden="true">
          <ProjectIcon strokeWidth={1.7} />
        </span>
        <div className="invite-card-project-text">
          <span className="invite-card-project-name" dir="auto">{invite.project_name}</span>
          {invite.project_description && (
            <span className="invite-card-project-desc bidi-clamp" dir="auto">{invite.project_description}</span>
          )}
        </div>
      </div>

      {invite.invite_message && (
        <blockquote className="invite-card-message">{invite.invite_message}</blockquote>
      )}

      {notification.reminded_at && pending && (
        <p className="invite-card-reminder">
          {t('projects.invite.reminded') || 'This is a reminder — the invitation is still open.'}
        </p>
      )}

      {error && <p className="invite-card-error" role="alert">{error}</p>}

      {pending ? (
        <div className="invite-card-actions">
          <button
            type="button"
            className="pj-btn pj-btn-secondary pj-btn-sm"
            onClick={() => respond(false)}
            disabled={!!busy}
          >
            {busy === 'decline'
              ? (t('common.saving') || 'Saving…')
              : (t('projects.invite.decline') || 'Decline')}
          </button>
          <button
            type="button"
            className="pj-btn pj-btn-primary pj-btn-sm"
            onClick={() => respond(true)}
            disabled={!!busy}
          >
            {busy === 'accept'
              ? (t('common.saving') || 'Saving…')
              : (t('projects.invite.accept') || 'Accept')}
          </button>
        </div>
      ) : (
        <div className="invite-card-actions">
          <span
            className={`pj-pill ${
              (resolved || notification.action_state) === 'accepted'
                ? 'pj-pill-accepted'
                : 'pj-pill-declined'
            }`}
          >
            {(resolved || notification.action_state) === 'accepted'
              ? (t('projects.invite.accepted') || 'Accepted')
              : (t('projects.invite.declined') || 'Declined')}
          </span>
          {(resolved || notification.action_state) === 'accepted' && (
            <button
              type="button"
              className="pj-btn pj-btn-secondary pj-btn-sm"
              onClick={() => onOpen(invite.project_id)}
            >
              {t('projects.invite.open') || 'Open project'}
            </button>
          )}
        </div>
      )}
      </>
      )}
    </article>
  );
}

export default InvitationCard;
